// import Logger from './logger';
import { API_CONFIG } from '../../config/api';  // Import the config
import { getSubscriptionTopic, getSocketPublishObject } from './utilities';
import { decodeMessage } from './message';

export class Octopus {
  /**
   * @param {Object} [initParams={}]
   * @param {string} [initParams.path] - e.g. '/mqtt'
   * @param {string} [initParams.loginId] - will auto generate some random string if not provided
   * @param {string} [initParams.userName] - can be used for auth, we are not using auth for mqtt as of now
   * @param {string} [initParams.password] - can be used for auth, we are not using auth for mqtt as of now
   *
   */
  constructor(initParams = {}) {
    const { path } = initParams;

    // The URI is built in connect() from the current login details, because this
    // instance is reused across reconnects (see disconnect()) and the token can change.
    this._path = path;
    this.uri = null;

    this._socket = null;
    this._reconnectionTimeout = 0;
    this._lastConnectedTimestamp = Date.now();
    this._topics = {};
    this._isTabVisible = document.visibilityState === 'visible';
    this._processingMessages = true; // Start with processing enabled
    this._isDestroyed = false; // Flag to stop reconnection entirely
    
    // Set up visibility change listener
    this._setupVisibilityListener();
    
    // this._maxCallbacksPerTopic = 10;
    this._sendHeartbeat = this._sendHeartbeat.bind(this);
    this.connect = this.connect.bind(this);
    this.wsHandler = this.wsHandler.bind(this);

    this._sendHeartbeat();
  }

  destroy() {
    console.log('Octopus: Destroying instance. Stopping all connections and timers.');
    this._isDestroyed = true;
    clearInterval(this._heartbeatInterval);
    document.removeEventListener('visibilitychange', this._onVisibilityChange);
    if (this._reconnectionTimeout) {
      clearTimeout(this._reconnectionTimeout);
    }
    if (this._socket) {
      this._socket.onclose = null; // Prevent onclose firing during manual close
      this._socket.onerror = null;
      this._socket.close();
    }
  }

  // Close only the socket. Keep this instance and its register (_topics) so that
  // connect() can reopen it and onopen re-subscribes every topic still in use.
  disconnect() {
    console.log('Octopus: Disconnecting socket. Subscriptions are kept for the next connect.');
    if (this._reconnectionTimeout) {
      clearTimeout(this._reconnectionTimeout);
      this._reconnectionTimeout = null;
    }
    if (this._socket) {
      // Detach handlers so this close does not trigger the auto-reconnect in onclose
      this._socket.onopen = null;
      this._socket.onmessage = null;
      this._socket.onclose = null;
      this._socket.onerror = null;
      this._socket.close();
      this._socket = null;
    }
  }

  _setupVisibilityListener() {
    // Kept on the instance so destroy() can remove it
    this._onVisibilityChange = () => {
      const wasVisible = this._isTabVisible;
      this._isTabVisible = document.visibilityState === 'visible';
      
      if (this._isTabVisible && !wasVisible) {
        // Tab has become visible
        console.log('Tab became visible, refreshing data...');
        
        // Start processing messages again
        this._processingMessages = true;
        
        // If socket is open, refresh subscriptions
        if (this._socket && this._socket.readyState === WebSocket.OPEN) {
          console.log('Refreshing subscriptions to get latest data');
          this.refreshSubscriptions();
        }
      } else if (!this._isTabVisible && wasVisible) {
        // Tab has become hidden
        console.log('Tab hidden, pausing message processing');
        
        // Stop processing messages to prevent data buildup
        this._processingMessages = false;
      }
    };
    document.addEventListener('visibilitychange', this._onVisibilityChange);
  }

  _sendHeartbeat() {
    // Kept on the instance so destroy() can clear it
    this._heartbeatInterval = setInterval(() => {
      if (this._socket && this._socket.readyState === WebSocket.OPEN) {
        this._socket.send('{"a":"h","v":[],"m":""}');
      }
    }, 9500);
  }

  _isAuthValid() {
    const loginId = localStorage.getItem('login_id');
    const token = localStorage.getItem('auth_token');
    return !!(loginId && token);
  }

  _buildUri() {
    const loginId = localStorage.getItem('login_id');
    const token = localStorage.getItem('auth_token');
    return `${API_CONFIG.WSS_URL}${this._path}?login_id=${loginId}&token=${token}&device=web`;
  }

  connect() {
    return new Promise((resolve, reject) => {
      // Guard: Don't connect if the user is not authenticated OR the instance is destroyed
      if (!this._isAuthValid() || this._isDestroyed) {
        console.error('Octopus: Refusing to connect. Session invalid or instance destroyed.');
        return reject('Connection refused');
      }

      // Idempotency guard — if a socket is already CONNECTING or OPEN, reuse
      // it instead of spawning another WebSocket. Multiple concurrent callers
      // (e.g. visibilitychange + a hook's wsHandler in the same tick) would
      // otherwise each construct a fresh WebSocket, leaving the previous one
      // leaking until GC.
      if (this._socket) {
        const rs = this._socket.readyState;
        if (rs === WebSocket.OPEN) return resolve('socket already connected');
        if (rs === WebSocket.CONNECTING) {
          this._socket.addEventListener('open', () => resolve('socket connected'), { once: true });
          this._socket.addEventListener('error', (e) => reject(e), { once: true });
          return;
        }
        // CLOSING or CLOSED — drop the reference and fall through to create a new one.
        this._socket = null;
      }

      this.uri = this._buildUri();
      this._socket = new WebSocket(this.uri);
      this._socket.binaryType = 'arraybuffer';

      this._socket.onopen = () => {
        resolve('socket connected');
        console.log('socket connected');
        
        // Reset timeout if success
        if (this._reconnectionTimeout) {
          clearTimeout(this._reconnectionTimeout);
          this._reconnectionTimeout = null;
        }
        
        // Only resubscribe if tab is visible
        if (this._isTabVisible) {
          Object.values(this._topics).forEach(({ subscriptionObj }) => {
            if (this._socket && this._socket.readyState === WebSocket.OPEN) {
              this._socket.send(subscriptionObj);
            }
          });
        }
      }

      this._socket.onmessage = (event) => {
        // Only process messages if tab is visible AND not destroyed
        if (!this._isTabVisible || !this._processingMessages || this._isDestroyed) {
          return; 
        }
        
        const buffer = event.data;
        try {
          const message = decodeMessage(buffer);
          const topic = message.topic;
          const subscriptions = (this._topics[topic] || {}).subscriptions || [];
          subscriptions.forEach(({ callback }) => callback(message));
        } catch (error) {
          console.log(error);
        }
      }

      this._socket.onclose = () => {
        // Only reconnect if tab is visible AND user is logged in AND not destroyed
        if (this._isTabVisible && this._isAuthValid() && !this._isDestroyed) {
          if (this._reconnectionTimeout) clearTimeout(this._reconnectionTimeout);
          this._reconnectionTimeout = setTimeout(() => this.connect(), 1000);
        } else {
          console.log('Octopus: Connection closed. Reconnection stopped (User logout or inactive).');
        }
      }

      this._socket.onerror = (err) => {
        // Logger(`Socket encountered error: ${err.message}, closing socket`);
        this._socket.close();
      }
    });
  }

  wsHandler({ messageType, subscriptionLocation, payload }) {
    const topic = getSubscriptionTopic({ messageType, payload });
    const partialSubscriptionObj = getSocketPublishObject({
      messageType,
      payload
    });
    const subscriptionObj = JSON.stringify({ ...partialSubscriptionObj, a: 'subscribe' });
    const unsubscriptionObj = JSON.stringify({ ...partialSubscriptionObj, a: 'unsubscribe' });
    return {
      subscribe: (callback) => {
        // debugger;
        return new Promise((resolve, reject) => {
          if (!this._topics[topic]) {
            this._topics[topic] = {
              subscriptionObj,
              subscriptions: [],
            };
          }
          const alreadySubscribed = Boolean(
            this._topics[topic].subscriptions
              .filter(obj => obj.subscriptionLocation === subscriptionLocation)
              .length
          )

          if (alreadySubscribed) {
            // reject(`${messageType} already subscribed from ${subscriptionLocation}`);
            return;
          }

          if (this._socket && this._socket.readyState === WebSocket.OPEN) {
            // console.log('this._socket.readyState',this._socket.readyState)
            if (this._topics[topic].subscriptions.length) {
              this._socket.send(unsubscriptionObj);
            }
            this._socket.send(subscriptionObj);
            resolve('subscribed');
          } else {
          
            // reject(`socket not connect, ${messageType} will be auto subscribed later`);
          }
          
          this._topics[topic].subscriptions.push({
            subscriptionLocation,
            callback,
          });

        });
      },
      unsubscribe: () => {
        return new Promise((resolve, reject) => {
          const subscriptions = (this._topics[topic] || []).subscriptions || [];
          const remainingSubscriptions =
            subscriptions
              .filter(obj => obj.subscriptionLocation !== subscriptionLocation);

          if (remainingSubscriptions.length === subscriptions.length) {
            // Logger(messageType, subscriptionLocation, payload);
            // reject('subscription does not exists');
          } else {
            resolve('unsubscribed');
            if (remainingSubscriptions.length === 0) {
              // Last listener left: drop the topic so reconnects don't re-subscribe it
              delete this._topics[topic];
              // A socket that isn't open has no server-side subscription to remove,
              // and send() throws while the socket is still connecting
              if (this._socket && this._socket.readyState === WebSocket.OPEN) {
                this._socket.send(unsubscriptionObj);
              }
            } else {
              this._topics[topic].subscriptions = remainingSubscriptions;
            }
          }
        });
      }
    }
  }
  
  // Method to check if connection is ready
  isConnected() {
    return this._socket && this._socket.readyState === WebSocket.OPEN;
  }
  
  // Method to refresh all subscriptions
  refreshSubscriptions() {
    if (this._socket && this._socket.readyState === WebSocket.OPEN) {
      console.log('Refreshing all WebSocket subscriptions');
      
      const topics = Object.keys(this._topics);
      console.log(`Found ${topics.length} active subscriptions to refresh`);
      
      if (topics.length === 0) {
        console.log('No subscriptions to refresh');
        return false;
      }
      
      Object.values(this._topics).forEach(({ subscriptionObj }) => {
        console.log(`Refreshing subscription: ${subscriptionObj}`);
        this._socket.send(subscriptionObj);
      });
      
      return true;
    }
    console.log('Cannot refresh subscriptions: WebSocket not connected');
    return false;
  }
}
